import React, { useState, useEffect } from 'react';
import { Box, Grid, Card, CardContent, Typography, CircularProgress, Alert, Button, ToggleButton, ToggleButtonGroup, Tooltip,  } from '@mui/material';
import {
  People as PeopleIcon,
  Receipt as InvoiceIcon,
  AttachMoney as MoneyIcon,
  Add as AddIcon,
  CalendarMonth as CalendarIcon,
  ShoppingCart as OrderIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { userInfoState } from '../../recoil/atoms';
import client from '../../api/client';

import defaultBg from '../../assets/images/dashboard-bg.jpg';

const themeColors = {
  cardBg: 'linear-gradient(135deg, #1a1a2e 0%, #2d2d44 100%)',
  gold: '#d4af37',
  goldLight: '#f4e4bc',
  text: '#ffffff',
  textMuted: 'rgba(255,255,255,0.7)',
};

const toPersianDate = () => {
  const now = new Date();
  const options = { 
    timeZone: 'Asia/Tehran',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'long',
  };
  return now.toLocaleDateString('fa-IR', options);
};

const toPersianTime = () => {
  const now = new Date();
  const options = { 
    timeZone: 'Asia/Tehran',
    hour: '2-digit',
    minute: '2-digit',
  };
  return now.toLocaleTimeString('fa-IR', options);
};

const StatCard = ({ title, value, icon, gradient, onClick, filter, onFilterChange }) => (
  <Card
    sx={{
      height: '100%',
      background: gradient,
      color: themeColors.text,
      boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
      border: `1px solid rgba(212, 175, 55, 0.3)`,
      transition: 'all 0.3s ease',
      '&:hover': { 
        boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
        border: `1px solid ${themeColors.gold}`,
      },
    }}
  >
    <CardContent sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {onFilterChange && (
        <Box sx={{ mb: 1, display: 'flex', justifyContent: 'flex-start' }}>
          <ToggleButtonGroup
            value={filter}
            exclusive
            onChange={(e, val) => val && onFilterChange(val)}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                color: themeColors.textMuted,
                borderColor: 'rgba(212, 175, 55, 0.3)',
                fontSize: '0.65rem',
                py: 0.2,
                px: 0.8,
                minWidth: 'auto',
                '&.Mui-selected': {
                  bgcolor: 'rgba(212, 175, 55, 0.2)',
                  color: themeColors.gold,
                },
              },
            }}
          >
            <ToggleButton value="today">امروز</ToggleButton>
            <ToggleButton value="month">ماه</ToggleButton>
            <ToggleButton value="year">سال</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          cursor: onClick ? 'pointer' : 'default',
          flexGrow: 1,
        }}
        onClick={onClick}
      >
        <Box>
          <Typography variant="caption" sx={{ color: themeColors.textMuted }}>
            {title}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: themeColors.goldLight }}>
            {value}
          </Typography>
        </Box>
        <Box
          sx={{
            p: 1,
            borderRadius: '50%',
            bgcolor: 'rgba(212, 175, 55, 0.2)',
            color: themeColors.gold,
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const formatNumber = (num) => {
  return new Intl.NumberFormat('fa-IR').format(num || 0);
};

const ShopDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(toPersianTime());
  const [invoiceFilter, setInvoiceFilter] = useState('today');
  const [salesFilter, setSalesFilter] = useState('today');
  const [filteredStats, setFilteredStats] = useState({ invoices: 0, sales: 0 });
  const [statsLoaded, setStatsLoaded] = useState(false);
  
  const navigate = useNavigate();
  const userInfo = useRecoilValue(userInfoState);
  const isSalesperson = userInfo?.role?.toLowerCase() === 'salesperson';

  const backgroundImage = userInfo?.shop_image 
    ? `${process.env.REACT_APP_API_URL || ''}${userInfo.shop_image}`
    : defaultBg;

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(toPersianTime());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    if (statsLoaded && !isSalesperson) {
      fetchFilteredStats();
    }
  }, [invoiceFilter, salesFilter, statsLoaded, isSalesperson]);

  const fetchStats = async () => {
    try {
      const response = await client.get('/users/dashboard/');
      setStats(response.data);
      setFilteredStats({
        invoices: response.data?.invoices_count || 0,
        sales: response.data?.total_sales || 0,
      });
      setStatsLoaded(true);
    } catch (error) {
      console.error('Error fetching stats:', error);
      setStats({
        customers_count: 0,
        invoices_count: 0,
        orders_count: 0,
        total_sales: 0,
      });
      setFilteredStats({ invoices: 0, sales: 0 });
      setStatsLoaded(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilteredStats = async () => {
    try {
      const [invoiceRes, salesRes] = await Promise.all([
        client.get('/users/dashboard/stats/', { 
          params: { type: 'invoices', filter: invoiceFilter } 
        }),
        client.get('/users/dashboard/stats/', { 
          params: { type: 'sales', filter: salesFilter } 
        })
      ]);
      
      setFilteredStats({
        invoices: invoiceRes.data?.count || 0,
        sales: salesRes.data?.total || 0,
      });
    } catch (error) {
      console.error('Error fetching filtered stats:', error);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress sx={{ color: themeColors.gold }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100%',
        p: 2,
        position: 'relative',
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(180deg, rgba(26,26,46,0.85) 0%, rgba(26,26,46,0.7) 100%)',
          zIndex: 0,
        },
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Typography 
          variant="h5" 
          sx={{ 
            mb: 1,
            fontWeight: 'bold',
            color: themeColors.gold,
            textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
          }}
        >
          داشبورد
        </Typography>

        <Box 
          sx={{ 
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 2,
            color: themeColors.textMuted,
          }}
        >
          <CalendarIcon sx={{ fontSize: 18, color: themeColors.gold }} />
          <Typography variant="body2">
            {toPersianDate()} | ساعت {currentTime}
          </Typography>
        </Box>

        {stats && !stats.is_subscribed && (
          <Alert 
            severity="warning" 
            sx={{ 
              mb: 2,
              bgcolor: 'rgba(255, 152, 0, 0.15)',
              color: '#ffb74d',
              border: '1px solid rgba(255, 152, 0, 0.3)',
              '& .MuiAlert-icon': { color: '#ffb74d' },
            }}
          >
            اشتراک شما فعال نیست. برای استفاده از تمام امکانات لطفاً اشتراک تهیه کنید.
          </Alert>
        )}

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/shop/orders/new')}
          sx={{ 
            mb: 3,
            py: 1.5,
            background: `linear-gradient(45deg, ${themeColors.gold} 30%, ${themeColors.goldLight} 90%)`,
            color: '#1a1a2e',
            fontWeight: 'bold',
            fontSize: '1rem',
            boxShadow: '0 4px 20px rgba(212, 175, 55, 0.4)',
            '&:hover': {
              background: `linear-gradient(45deg, #b8960c 30%, ${themeColors.gold} 90%)`,
            },
          }}
          fullWidth
          size="large"
        >
          + سفارش جدید
        </Button>

        {/* کارت‌های آمار */}
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <StatCard
              title="مشتریان"
              value={formatNumber(stats?.customers_count)}
              icon={<PeopleIcon sx={{ fontSize: 24 }} />}
              gradient="linear-gradient(135deg, #1a1a2e 0%, #2d2d44 100%)"
              onClick={() => navigate('/shop/customers')}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard
              title={isSalesperson ? "سفارشات فاکتور شده" : "سفارشات"}
              value={formatNumber(stats?.orders_count)}
              icon={<OrderIcon sx={{ fontSize: 24 }} />}
              gradient="linear-gradient(135deg, #2d2d44 0%, #1a1a2e 100%)"
              onClick={() => navigate('/shop/orders')}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard
              title={isSalesperson ? "پیش‌سفارش‌های من" : "فاکتورها"}
              value={formatNumber(isSalesperson ? stats?.invoices_count : (filteredStats.invoices || stats?.invoices_count))}
              icon={<InvoiceIcon sx={{ fontSize: 24 }} />}
              gradient="linear-gradient(135deg, #1a1a2e 0%, #2d2d44 100%)"
              onClick={isSalesperson ? () => navigate('/shop/orders') : () => navigate('/shop/invoices')}
              filter={isSalesperson ? null : invoiceFilter}
              onFilterChange={isSalesperson ? null : setInvoiceFilter}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard
              title="فروش (ریال)"
              value={formatNumber(isSalesperson ? 0 : (filteredStats.sales || stats?.total_sales))}
              icon={<MoneyIcon sx={{ fontSize: 24 }} />}
              gradient="linear-gradient(135deg, #1a472a 0%, #2d5a3d 100%)"
              onClick={isSalesperson ? null : () => navigate('/shop/stats')}
              filter={isSalesperson ? null : salesFilter}
              onFilterChange={isSalesperson ? null : setSalesFilter}
            />
          </Grid>
        </Grid>

        {/* آخرین سفارشات */}
        {stats?.recent_orders?.length > 0 && (
          <Card 
            sx={{ 
              mt: 3,
              background: themeColors.cardBg,
              color: themeColors.text,
              border: `1px solid rgba(212, 175, 55, 0.2)`,
            }}
          >
            <CardContent>
              <Typography 
                variant="h6" 
                sx={{ mb: 2, color: themeColors.gold, fontWeight: 'bold' }}
              >
                {isSalesperson ? 'سفارشات من' : 'آخرین سفارشات'}
              </Typography>
              {stats.recent_orders.map((order, index) => {
                // فروشنده فقط پیش‌سفارش‌های خودش رو می‌تونه کلیک کنه
                const canClick = !isSalesperson || order.status === 'pre_order';
                
                return (
                  <Box
                    key={order.id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 1.5,
                      borderBottom: index < stats.recent_orders.length - 1 
                        ? '1px solid rgba(212, 175, 55, 0.1)' 
                        : 'none',
                      cursor: canClick ? 'pointer' : 'default',
                      opacity: canClick ? 1 : 0.7,
                      '&:hover': canClick ? { bgcolor: 'rgba(212, 175, 55, 0.1)' } : {},
                    }}
                    onClick={() => canClick && navigate(`/shop/orders/${order.id}`)}
                  >
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: themeColors.goldLight }}>
                        {order.order_number}
                      </Typography>
                      <Typography variant="caption" sx={{ color: themeColors.textMuted }}>
                        {order.customer_name || 'بدون مشتری'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {!canClick && (
                        <Tooltip title="فقط پیش‌سفارش‌ها قابل مشاهده است">
                          <LockIcon sx={{ fontSize: 16, color: themeColors.textMuted }} />
                        </Tooltip>
                      )}
                      <Box sx={{ textAlign: 'left' }}>
                        <Typography variant="body2" sx={{ color: themeColors.gold }}>
                          {order.status_display}
                        </Typography>
                        <Typography variant="caption" sx={{ color: themeColors.textMuted }}>
                          {order.measurements_count} اندازه‌گیری
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </CardContent>
          </Card>
        )}
      </Box>
    </Box>
  );
};

export default ShopDashboard;